import React, { useEffect, useState } from "react";
import { GoogleOAuthProvider } from "@react-oauth/google";
import LenderApp from "./lender/App";
import BorrowerApp from "./borrower/App";
import ErrorBoundary from "./shared/components/ErrorBoundary";
import { enforceRoleAccess } from "./shared/utils/roleManager";
import { ThemeProvider } from "./shared/contexts/ThemeContext";
import { BrandingProvider } from "./shared/contexts/BrandingContext";
import { isEmbedded } from "./shared/utils/embedDetection";
import "./shared/styles/theme.css";
import "./lender/index.css";

const getViewFromPath = () => (window.location.pathname.startsWith("/borrower") ? "borrower" : "lender");

const App = () => {
  const [view, setView] = useState(getViewFromPath());
  const [isAuthChecked, setIsAuthChecked] = useState(false);

  // Enforce role-based access control BEFORE rendering
  useEffect(() => {
    const currentPath = window.location.pathname;

    // IMPORTANT: Allow unauthenticated access to borrower portal
    // The borrower portal uses case-number-based authentication, not token-based
    // Borrowers authenticate by entering their case number, not by logging in first
    if (currentPath.startsWith("/borrower")) {
      // Skip role enforcement for borrower portal - it handles its own auth flow
      setIsAuthChecked(true);
      return;
    }

    // Run the full role access enforcement for lender paths only
    enforceRoleAccess(currentPath);
    setIsAuthChecked(true);
  }, []);

  // Re-check on view changes (only for lender paths)
  useEffect(() => {
    if (isAuthChecked) {
      const currentPath = window.location.pathname;
      // Skip role enforcement for borrower portal - it handles its own auth flow
      if (!currentPath.startsWith("/borrower")) {
        enforceRoleAccess(currentPath);
      }
    }
  }, [view, isAuthChecked]);

  useEffect(() => {
    document.body.dataset.appView = view;
    return () => {
      delete document.body.dataset.appView;
    };
  }, [view]);

  // When embedded in an iframe, apply CSS to prevent scroll propagation to parent
  useEffect(() => {
    if (isEmbedded) {
      // Add class to document for CSS targeting
      document.documentElement.classList.add('is-embedded');
      document.body.classList.add('is-embedded');

      return () => {
        document.documentElement.classList.remove('is-embedded');
        document.body.classList.remove('is-embedded');
      };
    }
  }, []);

  useEffect(() => {
    const handlePop = () => setView(getViewFromPath());
    window.addEventListener("popstate", handlePop);
    return () => window.removeEventListener("popstate", handlePop);
  }, []);

  const switchView = (next) => {
    setView(next);
    const path = next === "borrower" ? "/borrower" : "/";
    window.history.pushState({}, "", path);
  };

  const isBorrower = view === "borrower";

  // Don't render borrower app until auth is checked
  if (isBorrower && !isAuthChecked) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-white">Checking authentication...</div>
      </div>
    );
  }

  return (
    <ThemeProvider>
      <BrandingProvider>
        <ErrorBoundary>
          <div className="min-h-screen bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-50 transition-colors duration-300">
            {isBorrower ? (
              <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
                <BorrowerApp />
              </GoogleOAuthProvider>
            ) : (
              <LenderApp />
            )}
          </div>
        </ErrorBoundary>
      </BrandingProvider>
    </ThemeProvider>
  );
};

export default App;
